/**
 * MobileTableSectionHeader — the LOCKED flush section header for the table screens
 * (Roster + team Stats + the Team-Dashboard Box Score). No emoji, no box, no
 * rounded tile: a 3px accent bar + a 12px/600 uppercase accent-colored label, an
 * optional muted count right after it, and a right-pushed group that can hold an
 * inline Hitters/Pitchers `tabs` toggle, an `action` (e.g. Stats' Totals/3-Line
 * button), and/or a ▶/▼ collapse chevron. The table sits flush beneath it.
 *
 * color    = the section accent (label + bar; gold primary by default; Roster
 *            varies it per section). barColor overrides JUST the bar (Dashboard
 *            Box Score = green bar + gold label). Tokens only — never hardcode.
 * tabs     = { active, onSelect, options:[{key,label}] } → inline right-aligned
 *            active/inactive tabs (active = primary + an absolute 2px underline
 *            that adds ZERO layout height, so the tab text shares the label's
 *            centerline).
 *
 * Every section header uses the SAME symmetric vertical padding (9px top/bottom)
 * — no first-header special-case — so all sections are spaced identically.
 */
import React from 'react';
import { dynastyTheme } from '../../../services/colorService';

const C = dynastyTheme.tokens.colors;
const GOLD = dynastyTheme.tokens.colors.primary;

const MobileTableSectionHeader = ({ label, color = GOLD, barColor, count, collapsed, onToggle, action, tabs }) => (
  <div
    onClick={onToggle}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '9px 0', // canonical 9px symmetric buffer (was 6)
      cursor: onToggle ? 'pointer' : 'default',
    }}
  >
    <span aria-hidden="true" style={{ width: 3, height: 14, borderRadius: 2, background: barColor || color, flexShrink: 0 }} />
    {/* Label + count share a flex:1 / minWidth:0 group: it takes only the LEFTOVER
        space (basis 0) and the label ellipsis-truncates, so a long
        "Box Score · <date>" can NEVER push the flexShrink:0 right group (tabs /
        action / chevron) past the row's right edge. The count stays attached. */}
    <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <span style={{ textTransform: 'uppercase', letterSpacing: '1px', fontSize: 12, fontWeight: 600, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}>
        {label}
      </span>
      {count != null && (
        <span style={{ fontSize: 9, color: C.neutral[500], marginLeft: 6, flexShrink: 0 }}>{count}</span>
      )}
    </span>
    {(action || onToggle || tabs) && (
      // paddingRight:6 on the tabs' OWN flex container — this is the element whose
      // right edge "Pitchers" (the last tab, no padding of its own) sits against, so
      // its padding-right reserves 6px INSIDE it, after the last tab → "Pitchers"
      // lands 6px in from this group's right edge (which the flex layout puts at the
      // header's content edge = the section's content edge). The inset lives on the
      // element that holds the tabs, not on a parent (header padding, 5c970f28) or a
      // sibling — those don't bind where the tab text actually renders.
      <span
        style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0, paddingRight: 8 }}
        onClick={(action || tabs) ? (e) => e.stopPropagation() : undefined}
      >
        {tabs && tabs.options.map((opt) => {
          const active = tabs.active === opt.key;
          return (
            <span
              key={opt.key}
              onClick={(e) => { e.stopPropagation(); tabs.onSelect(opt.key); }}
              style={{
                position: 'relative',
                fontSize: 10.5,
                fontWeight: active ? 800 : 700,
                color: active ? C.primary : C.neutral[400],
                cursor: 'pointer',
              }}
            >
              {opt.label}
              {active && (
                <span
                  aria-hidden="true"
                  style={{ position: 'absolute', left: 0, right: 0, bottom: -3, height: 2, borderRadius: 1, background: C.primary }}
                />
              )}
            </span>
          );
        })}
        {action}
        {onToggle && (
          <span aria-hidden="true" style={{ color: C.muted, fontSize: 11 }}>{collapsed ? '▶' : '▼'}</span>
        )}
      </span>
    )}
  </div>
);

export default MobileTableSectionHeader;
